<script setup lang="ts">
import { computed, ref } from 'vue'

import clsx from 'clsx'

import { useEditorBetaStore } from '@/stores/editor/beta'

const props = defineProps<{
    element: any
    elementIndex: number
    isDarkMode: boolean
    schema?: any
}>()

const betaStore = useEditorBetaStore()
const elementId = computed(() => betaStore.getElementId(props.element))
const isActive = computed(() => betaStore.isElementActive(elementId.value))
const viewCode = ref(false)
const previewFrame = ref<HTMLIFrameElement | null>(null)

const onChangeContent = (event: Event) => {
    betaStore.patchElementBySchema(props.schema, {
        frameContent: (event.target as HTMLTextAreaElement).value,
    })
}

const resizeIframe = () => {
    const iframe = previewFrame.value

    if (!iframe) return

    try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document
        if (doc) iframe.style.height = `${doc.documentElement.scrollHeight}px`
    } catch (error) {
        console.warn('Não foi possível ajustar o iframe:', error)
    }
}
</script>

<template>
    <div
        tabindex="0"
        :id="'betaEditorScript-' + elementIndex"
        :class="clsx('editor-script-beta')"
        :data-active="isActive"
        :schema="JSON.stringify(schema)"
    >
        <button
            v-if="isActive"
            type="button"
            :class="clsx('editor-script-beta__toggle', isDarkMode ? 'dark' : 'light')"
            @click.stop="viewCode = !viewCode"
        >
            <span class="sr-only">{{ viewCode ? 'Ver preview' : 'Ver código' }}</span>
            <svg v-if="viewCode" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
            </svg>
        </button>

        <div v-if="viewCode" class="h-auto w-full p-4">
            <textarea
                class="input-element-script h-80 w-full appearance-none rounded border border-[#DCE5F2] bg-white p-4 font-mono text-sm outline-none"
                :value="element.frameContent"
                @input="onChangeContent"
            />
        </div>

        <div v-else class="relative h-full w-full">
            <div :class="clsx('absolute inset-0 z-10 opacity-30', isDarkMode ? 'bg-white' : 'bg-black')">
                <div class="flex h-full w-full items-center justify-center">
                    <svg :class="clsx('h-14 w-14', isDarkMode ? 'stroke-black' : 'stroke-white')" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
                    </svg>
                </div>
            </div>

            <iframe
                ref="previewFrame"
                class="w-full border-0"
                :srcdoc="element.frameContent"
                sandbox="allow-same-origin allow-scripts allow-forms"
                @load="resizeIframe"
            />
        </div>
    </div>
</template>

<style scoped>
.editor-script-beta {
    @apply relative min-h-20 border-2 border-transparent bg-transparent outline-none transition-all duration-1000 ease-in-out hover:border-[#D7E3F4] hover:shadow-[0_0_0_2px_rgba(215,227,244,0.22)];
}

.editor-script-beta[data-active='true'] {
    @apply border-[#8EB3E8] shadow-[0_0_0_3px_rgba(142,179,232,0.24)];
}

.editor-script-beta__toggle {
    @apply absolute right-3 top-3 z-[75] flex h-8 w-8 items-center justify-center rounded-full border border-[#D7D7D7] bg-[rgba(245,245,245,0.96)] opacity-80 shadow-sm hover:opacity-100;
}

.editor-script-beta__toggle.light svg {
    @apply stroke-[#060606];
}

.editor-script-beta__toggle.dark svg {
    @apply stroke-[#FAFAFA];
}
</style>
